// src/pages/cocos/CocoDashboard.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  writeBatch, query, orderBy, where, deleteDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

// ─── Survey question IDs (hardcoded from the coco survey) ──────────────────
const Q_GRADE    = '64bbd7b7-40fd-4e71-8ea1-652b585cf9d0';
const Q_BBALL    = 'cd4d0a7e-2f7b-4c35-bb97-f3c08b070763';
const Q_TEAM     = '230adb8e-393c-4462-bca3-221346fcfeac';
const Q_CHOICE1  = '76874998-90a7-45fe-a383-62bd147a7b78';
const Q_CHOICE2  = '8a45c347-baff-452b-bba7-b99a24268725';
const Q_CHOICE3  = 'e89620ad-1f53-4557-96dd-3ad3aa60c67b';

// Base activity names (what students see / choose).
// These are seeded into Firestore on first load if not already present.
const ACTIVITIES = [
  'Gym/fitness', 'Fit Walking', 'Golf', 'Squash', 'Padel', 'Tennis',
  'Swimming', 'Boxing', 'Ultimate Frisbee', 'Horseback Riding',
  'Fencing', 'Dabke & Contemporary Dance', 'Yoga',
  'Offseason Football', 'Offseason Volleyball',
];

// Convert a display name + session into a safe Firestore document ID.
// Strips characters that Firestore disallows in path segments.
// e.g. "Gym/fitness" session 1 → "Gym-fitness--s1"
//      "Dabke & Contemporary Dance" session 2 → "Dabke---Contemporary-Dance--s2"
function toDocId(name, session = 1) {
  const safe = name.replace(/\//g, '-').replace(/&/g, '-').replace(/\s+/g, '-').replace(/-{2,}/g, '-').replace(/^-|-$/g, '');
  return `${safe}--s${session}`;
}

// Derive a human-readable display name from a doc ID when the name field is missing.
// e.g. "Boxing--s1" → "Boxing", "Fit-Walking--s1" → "Fit Walking"
function nameFromDocId(docId) {
  return (docId || '')
    .replace(/--s\d+$/, '')   // strip --s1, --s2 suffix
    .replace(/-/g, ' ')        // dashes → spaces
    .trim();
}

// Get the best display name for an activity doc
function actName(docId, act) {
  return act?.name || nameFromDocId(docId);
}

// Reverse: given a doc ID, return the display label
// We store `name` and `session` on each activity doc so we don't need to reverse.

const TEAM_SPORT_VALUES = ['Volleyball', 'Football', 'Both Volleyball and Football'];

const STATUS_LABELS = {
  active:           { label: 'In Pool',         color: '#2563eb' },
  basketball:       { label: 'Basketball Tryout', color: '#d97706' },
  exempt:           { label: 'Exempt',           color: '#6b7280' },
  offseason:        { label: 'Offseason Training', color: '#7c3aed' },
  'offseason-opted-in': { label: 'Offseason → Cocos', color: '#059669' },
  unplaced:         { label: 'Unplaced',         color: '#dc2626' },
};

const SEASON_ID = 'semester2-2025-2026';

// ─── Styles ────────────────────────────────────────────────────────────────
const s = {
  page: { maxWidth: 1200, margin: '0 auto', padding: '28px 24px', fontFamily: 'var(--font-body)' },
  h1:   { fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 4 },
  sub:  { fontSize: 13, color: 'var(--ka-gray-500)', marginBottom: 28 },
  tabs: { display: 'flex', gap: 4, borderBottom: '2px solid var(--ka-gray-200)', marginBottom: 28 },
  tab:  (active) => ({
    padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: 'none', borderBottom: active ? '2px solid var(--ka-navy)' : '2px solid transparent',
    color: active ? 'var(--ka-navy)' : 'var(--ka-gray-500)', marginBottom: -2,
  }),
  card:   { background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)', borderRadius: 10, padding: 20, marginBottom: 16 },
  row:    { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  label:  { fontSize: 12, fontWeight: 600, color: 'var(--ka-gray-500)', marginBottom: 4, display: 'block' },
  input:  { padding: '7px 10px', border: '1px solid var(--ka-gray-300)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--font-body)' },
  select: { padding: '7px 10px', border: '1px solid var(--ka-gray-300)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--font-body)', background: 'var(--ka-white)' },
  btn:    (variant = 'primary') => ({
    padding: '8px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: variant === 'primary' ? 'var(--ka-navy)' : variant === 'danger' ? '#dc2626' : variant === 'success' ? '#059669' : 'var(--ka-gray-100)',
    color: variant === 'ghost' ? 'var(--ka-gray-700)' : '#fff',
  }),
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: color + '20', color: color,
  }),
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:    { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--ka-gray-200)', fontWeight: 600, color: 'var(--ka-gray-600)', fontSize: 12 },
  td:    { padding: '8px 10px', borderBottom: '1px solid var(--ka-gray-100)', verticalAlign: 'middle' },
  stat:  { textAlign: 'center', padding: '12px 20px', background: 'var(--ka-gray-50)', borderRadius: 8 },
  statN: { fontSize: 28, fontWeight: 700, color: 'var(--ka-navy)' },
  statL: { fontSize: 11, color: 'var(--ka-gray-500)', marginTop: 2 },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 12 },
  warn: { background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#92400e', marginBottom: 12 },
  success: { background: '#d1fae5', border: '1px solid #059669', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#065f46', marginBottom: 12 },
};

// ─── Helpers ───────────────────────────────────────────────────────────────

// Deduplicate a student's three activity choices.
// If choice2 matches choice1, blank it out; if choice3 matches either earlier
// unique choice, blank it out.  Returns { choice1, choice2, choice3 }.
function dedupeChoices(c1, c2, c3) {
  const v1 = (c1 || '').trim();
  const v2 = (c2 || '').trim();
  const v3 = (c3 || '').trim();
  const seen = new Set();
  const pick = (v) => { if (v && !seen.has(v)) { seen.add(v); return v; } return ''; };
  return { choice1: pick(v1), choice2: pick(v2), choice3: pick(v3) };
}

function parseCSVToEmails(text) {
  return text.split(/\r?\n/).flatMap(line =>
    line.split(',').map(v => v.trim().toLowerCase()).filter(v => v.includes('@'))
  );
}

// Build the initial activities map from the ACTIVITIES list.
// Each base name gets session 1 by default; duplication adds session 2, 3, etc.
function defaultActivitiesMap() {
  const map = {};
  ACTIVITIES.forEach(name => {
    const id = toDocId(name, 1);
    map[id] = { name, session: 1, capacity: 20, staff: [] };
  });
  return map;
}

// ─── Main Component ────────────────────────────────────────────────────────
const SUPERADMIN_UID = 'IOj8t19zqBRJynu45HRKqf8J0MB2';

export default function CocoDashboard() {
  const { isSuperAdmin, profile, user } = useAuth();
  const canManage = isSuperAdmin || profile?.role === 'admin' || user?.uid === SUPERADMIN_UID;

  const [tab, setTab] = useState('setup');
  const [config, setConfig] = useState(null);
  const [roster, setRoster] = useState({});         // { sisId: rosterDoc }
  const [activities, setActivities] = useState({}); // { docId: { name, session, capacity, staff[] } }
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);             // { type: 'success'|'warn', text }
  const [saving, setSaving] = useState(false);

  // ── Load from Firestore ─────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    const [cfgSnap, rosterSnap, actSnap] = await Promise.all([
      getDoc(doc(db, 'cocoSeasons', SEASON_ID)),
      getDocs(collection(db, 'cocoSeasons', SEASON_ID, 'roster')),
      getDocs(collection(db, 'cocoSeasons', SEASON_ID, 'activities')),
    ]);
    setConfig(cfgSnap.exists() ? cfgSnap.data() : { status: 'draft', label: 'Semester 2 2025–2026' });
    const r = {};
    rosterSnap.docs.forEach(d => { r[d.id] = d.data(); });
    setRoster(r);

    // Build activity map from Firestore, filling in defaults for any missing fields
    const a = {};
    const repairBatch = writeBatch(db);
    let needsRepair = false;
    actSnap.docs.forEach(d => {
      const data = d.data();
      // Backfill name if missing — derive from doc ID
      if (!data.name) {
        const derived = nameFromDocId(d.id);
        const session = parseInt((d.id.match(/--s(\d+)$/) || [])[1] || '1', 10);
        data.name = derived;
        data.session = data.session || session;
        repairBatch.update(doc(db, 'cocoSeasons', SEASON_ID, 'activities', d.id), { name: derived, session: data.session });
        needsRepair = true;
      }
      a[d.id] = data;
    });
    if (needsRepair) await repairBatch.commit();
    // Ensure every default session-1 activity is present
    ACTIVITIES.forEach(name => {
      const id = toDocId(name, 1);
      if (!a[id]) a[id] = { name, session: 1, capacity: 20, staff: [] };
    });
    setActivities(a);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  if (loading) return <div style={s.page}><p style={{ color: 'var(--ka-gray-500)' }}>Loading…</p></div>;

  const rosterList = Object.entries(roster).map(([id, d]) => ({ id, ...d }));
  const committed = config?.status === 'committed';

  // Sorted activity list: by name then by session number
  const activityList = Object.entries(activities).sort(([, a], [, b]) =>
    (a.name || '').localeCompare(b.name || '') || (a.session || 1) - (b.session || 1)
  );

  return (
    <div style={s.page}>
      <div style={s.row}>
        <div>
          <h1 style={s.h1}>Co-Curricular Scheduler</h1>
          <p style={s.sub}>{config?.label} · {rosterList.length} students loaded</p>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          {committed
            ? <span style={s.badge('#059669')}>✓ Committed</span>
            : <span style={s.badge('#d97706')}>Draft</span>}
        </div>
      </div>

      {msg && <div style={msg.type === 'success' ? s.success : s.warn}>{msg.text}</div>}

      <div style={s.tabs}>
        {[['setup', 'Setup & Roster'], ['activities', 'Activities & Staff'], ['scheduler', 'Scheduler'], ['results', 'Results']].map(([k, l]) => (
          <button key={k} style={s.tab(tab === k)} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'setup' && (
        <SetupTab
          config={config} roster={roster} rosterList={rosterList}
          setRoster={setRoster} setConfig={setConfig}
          canManage={canManage} flash={flash} saving={saving} setSaving={setSaving}
        />
      )}
      {tab === 'activities' && (
        <ActivitiesTab
          activities={activities} activityList={activityList}
          setActivities={setActivities}
          roster={roster} setRoster={setRoster}
          canManage={canManage} flash={flash} saving={saving} setSaving={setSaving}
        />
      )}
      {tab === 'scheduler' && (
        <SchedulerTab
          roster={roster} setRoster={setRoster}
          activities={activities} activityList={activityList}
          config={config} setConfig={setConfig}
          canManage={canManage} flash={flash} committed={committed}
          saving={saving} setSaving={setSaving}
        />
      )}
      {tab === 'results' && (
        <ResultsTab
          roster={roster} rosterList={rosterList} activities={activities} activityList={activityList}
          config={config} flash={flash} saving={saving} setSaving={setSaving}
          canManage={canManage} setRoster={setRoster}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 1 — Setup & Roster
// ═══════════════════════════════════════════════════════════════════════════
function SetupTab({ config, roster, rosterList, setRoster, setConfig, canManage, flash, saving, setSaving }) {
  const [surveyId, setSurveyId] = useState(config?.surveyId || '');
  const [exemptSurveyId, setExemptSurveyId] = useState(config?.exemptionsSurveyId || '');
  const [ingestingMain, setIngestingMain] = useState(false);
  const [ingestingExempt, setIngestingExempt] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [teamSport, setTeamSport] = useState('Volleyball');
  const [manualExemptText, setManualExemptText] = useState('');
  const [manualExemptReason, setManualExemptReason] = useState('');
  const [manualExempting, setManualExempting] = useState(false);
  const [manualExemptNotFound, setManualExemptNotFound] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGrade, setFilterGrade] = useState('all');
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const fileRef = useRef();

  // Available surveys loaded from Firestore
  const [availableSurveys, setAvailableSurveys] = useState([]);
  const [surveysLoaded, setSurveysLoaded] = useState(false);
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const [loadingAllStudents, setLoadingAllStudents] = useState(false);

  const loadSurveys = async () => {
    setLoadingSurveys(true);
    try {
      const snap = await getDocs(query(collection(db, 'surveys'), orderBy('createdAt', 'desc')));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAvailableSurveys(list);
    } catch (err) {
      // createdAt index may not exist — fall back to unordered
      try {
        const snap = await getDocs(collection(db, 'surveys'));
        setAvailableSurveys(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        flash('warn', 'Could not load surveys: ' + e.message);
      }
    }
    setSurveysLoaded(true);
    setLoadingSurveys(false);
  };

  // ── Load all students from /students and merge into roster ─────────────
  // Students already in the roster keep their existing status/choices.
  // Students not yet in the roster are added with status 'active' (no survey response).
  const loadAllStudents = async () => {
    setLoadingAllStudents(true);
    try {
      const snap = await getDocs(collection(db, 'students'));
      const batch = writeBatch(db);
      const updated = { ...roster };
      let added = 0;

      const UPPER_SCHOOL_GRADES = ['Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];

      snap.docs.forEach(d => {
        const data = d.data();

        // Only active upper-school students.
        // A record is excluded if EITHER:
        //   - active field is explicitly false
        //   - status field is explicitly 'inactive'
        // A record is included only if at least one active signal is present
        //   (status === 'active' OR active === true) and no inactive signal overrides.
        const statusInactive = data.status === 'inactive';
        const activeFieldFalse = data.active === false;
        const hasActiveSignal = data.status === 'active' || data.active === true;
        const isActive = hasActiveSignal && !statusInactive && !activeFieldFalse;
        const grade = data.grade || data.gradeLevel || '';
        const isUpperSchool = UPPER_SCHOOL_GRADES.includes(grade);
        if (!isActive || !isUpperSchool) return;

        const sisId = data.studentSisId || d.id;
        const email = (data.email || data.emailLower || '').toLowerCase();

        // Check if already in roster by sisId or email
        const alreadyInRoster =
          updated[sisId] ||
          Object.values(updated).some(r => (r.email || '').toLowerCase() === email && email);

        if (!alreadyInRoster) {
          const rosterDoc = {
            name: data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            email: data.email || data.emailLower || '',
            emailLower: email,
            grade,
            sisId,
            cohort: data.cohort || '',   // "Day" or "Boarding"
            choice1: '', choice2: '', choice3: '',
            basketball: false,
            teamSport: null,
            status: 'active',
            placed: null,
            placedRound: null,
            manualOverride: null,
            responseId: null,
            ingestedAt: new Date().toISOString(),
            source: 'students-collection',
          };
          batch.set(doc(db, 'cocoSeasons', SEASON_ID, 'roster', sisId), rosterDoc, { merge: true });
          updated[sisId] = rosterDoc;
          added++;
        }
      });

      await batch.commit();
      setRoster(updated);
      flash('success', `Loaded all students. Added ${added} new to roster (${Object.keys(updated).length} total).`);
    } catch (err) {
      flash('warn', 'Failed to load students: ' + err.message);
    }
    setLoadingAllStudents(false);
  };

  // ── Purge non-upper-school / inactive students from roster ─────────────
  const [purging, setPurging] = useState(false);
  const UPPER_SCHOOL_GRADES = ['Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];

  const purgeNonUpperSchool = async () => {
    setPurging(true);
    flash('info', 'Checking students against live directory…');

    try {
      // Re-fetch the full /students collection to get live active/grade values,
      // since roster docs may have stale copies of those fields.
      const snap = await getDocs(collection(db, 'students'));
      const liveStudents = {};
      snap.docs.forEach(d => { liveStudents[d.id] = d.data(); });

      const toRemove = Object.entries(roster).filter(([id, s]) => {
        // Check live data first; fall back to what's on the roster doc
        const live = liveStudents[id] || liveStudents[s.sisId] || null;
        const data = live || s;

        const grade = data.grade || data.gradeLevel || s.grade || '';
        const badGrade = grade && !UPPER_SCHOOL_GRADES.includes(grade);
        const noGrade  = !grade;

        // Active check against live data: explicitly inactive = remove
        const statusInactive = data.status === 'inactive';
        const activeFieldFalse = data.active === false;
        const hasActiveSignal = data.status === 'active' || data.active === true;
        // If we have live data, apply strict check; if only roster data, use looser check
        const isInactive = live
          ? (statusInactive || activeFieldFalse || !hasActiveSignal)
          : (s.active === false || s.status === 'inactive');

        return badGrade || noGrade || isInactive;
      });

      if (toRemove.length === 0) {
        flash('success', 'Roster is clean — all students are active upper-school.');
        setPurging(false);
        return;
      }

      if (!window.confirm(`Remove ${toRemove.length} students (non-upper-school or inactive per live directory)? This cannot be undone.`)) {
        setPurging(false);
        return;
      }

      // Firestore batch deletes (max 500 per batch)
      const chunks = [];
      for (let i = 0; i < toRemove.length; i += 500) chunks.push(toRemove.slice(i, i + 500));
      for (const chunk of chunks) {
        const b = writeBatch(db);
        chunk.forEach(([id]) => b.delete(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id)));
        await b.commit();
      }

      const updated = { ...roster };
      toRemove.forEach(([id]) => delete updated[id]);
      setRoster(updated);
      flash('success', `Removed ${toRemove.length} ineligible students. ${Object.keys(updated).length} remain.`);
    } catch (err) {
      flash('warn', 'Purge failed: ' + err.message);
    }
    setPurging(false);
  };

  // ── Clear entire roster (nuke + re-ingest from scratch) ────────────────
  const [clearing, setClearing] = useState(false);

  const clearRoster = async () => {
    if (!window.confirm(
      `Delete ALL ${rosterList.length} students from the roster?\n\nThis cannot be undone. You will need to re-ingest the survey to repopulate it.`
    )) return;
    setClearing(true);
    flash('info', 'Clearing roster…');
    try {
      const ids = Object.keys(roster);
      const chunks = [];
      for (let i = 0; i < ids.length; i += 500) chunks.push(ids.slice(i, i + 500));
      for (const chunk of chunks) {
        const b = writeBatch(db);
        chunk.forEach(id => b.delete(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id)));
        await b.commit();
      }
      setRoster({});
      flash('success', `Cleared ${ids.length} students. Roster is now empty — re-ingest to repopulate.`);
    } catch (err) {
      flash('warn', 'Clear failed: ' + err.message);
    }
    setClearing(false);
  };

  // ── Ingest main survey ──────────────────────────────────────────────────
  const ingestMainSurvey = async () => {
    if (!surveyId.trim()) return flash('warn', 'Enter the main survey ID first.');
    setIngestingMain(true);
    try {
      // Save survey ID to config
      await setDoc(doc(db, 'cocoSeasons', SEASON_ID), {
        surveyId: surveyId.trim(),
        label: 'Semester 2 2025–2026',
        status: config?.status || 'draft',
      }, { merge: true });
      setConfig(c => ({ ...c, surveyId: surveyId.trim() }));

      // Fetch survey responses
      const responsesSnap = await getDocs(
        query(collection(db, 'surveys', surveyId.trim(), 'responses'), orderBy('submittedAt', 'desc'))
      );

      if (responsesSnap.empty) { flash('warn', 'No responses found for that survey ID.'); setIngestingMain(false); return; }

      // Build email→sisId and email→cohort maps via /students email lookup
      const responses = responsesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const emails = [...new Set(responses.map(r => r.respondentEmail?.toLowerCase()).filter(Boolean))];
      const emailToSisId = {};
      const emailToCohort = {};
      for (let i = 0; i < emails.length; i += 10) {
        const chunk = emails.slice(i, i + 10);
        const snap = await getDocs(query(collection(db, 'students'), where('emailLower', 'in', chunk)));
        snap.docs.forEach(d => {
          const data = d.data();
          const key = (data.emailLower || data.email || '').toLowerCase();
          if (key) {
            emailToSisId[key] = data.studentSisId || d.id;
            emailToCohort[key] = data.cohort || '';
          }
        });
      }
      // Fallback: query by email field (mixed case) for misses
      const misses = emails.filter(e => !emailToSisId[e]);
      if (misses.length > 0) {
        for (let i = 0; i < misses.length; i += 10) {
          const chunk = misses.slice(i, i + 10);
          const snap = await getDocs(query(collection(db, 'students'), where('email', 'in', chunk)));
          snap.docs.forEach(d => {
            const data = d.data();
            const key = (data.email || '').toLowerCase();
            if (key) {
              emailToSisId[key] = data.studentSisId || d.id;
              emailToCohort[key] = data.cohort || '';
            }
          });
        }
        // Last resort: mixed-case originals
        const stillMissing = emails.filter(e => !emailToSisId[e]);
        if (stillMissing.length > 0) {
          const originalEmails = [...new Set(responses.map(r => r.respondentEmail).filter(Boolean))];
          for (let i = 0; i < originalEmails.length; i += 10) {
            const chunk = originalEmails.slice(i, i + 10);
            const snap = await getDocs(query(collection(db, 'students'), where('email', 'in', chunk)));
            snap.docs.forEach(d => {
              const data = d.data();
              const key = (data.email || '').toLowerCase();
              if (key && !emailToSisId[key]) {
                emailToSisId[key] = data.studentSisId || d.id;
                emailToCohort[key] = data.cohort || '';
              }
            });
          }
        }
      }

      // Write roster docs
      const batch = writeBatch(db);
      const newRoster = { ...roster };
      let written = 0;

      for (const r of responses) {
        const emailKey = r.respondentEmail?.toLowerCase() || '';
        const sisId = emailToSisId[emailKey] || null;
        const docId = sisId || r.id; // fall back to response ID if no sisId found

        const basketball = r.answers?.[Q_BBALL] === 'Yes';
        const teamAnswer = r.answers?.[Q_TEAM] || '';
        const isTeamSport = TEAM_SPORT_VALUES.some(v => teamAnswer.includes(v.split(' ')[0]));

        let status = 'active';
        if (basketball) status = 'basketball';
        else if (isTeamSport) status = 'offseason';

        const rosterDoc = {
          name: r.respondentName || '',
          email: r.respondentEmail || '',
          emailLower: emailKey,
          grade: r.answers?.[Q_GRADE] || '',
          sisId: sisId || '',
          cohort: emailToCohort[emailKey] || roster[docId]?.cohort || '',  // "Day" or "Boarding"
          ...dedupeChoices(r.answers?.[Q_CHOICE1], r.answers?.[Q_CHOICE2], r.answers?.[Q_CHOICE3]),
          basketball,
          teamSport: isTeamSport ? teamAnswer : null,
          status,
          placed: null,
          placedRound: null,
          manualOverride: null,
          responseId: r.id,
          ingestedAt: new Date().toISOString(),
        };

        batch.set(doc(db, 'cocoSeasons', SEASON_ID, 'roster', docId), rosterDoc, { merge: false });
        newRoster[docId] = rosterDoc;
        written++;
      }

      await batch.commit();
      setRoster(newRoster);
      flash('success', `Ingested ${written} responses. ${Object.values(newRoster).filter(r => !r.sisId).length} students missing SIS ID.`);
    } catch (err) {
      console.error(err);
      flash('warn', 'Ingest failed: ' + err.message);
    }
    setIngestingMain(false);
  };

  // ── Ingest exemptions survey ────────────────────────────────────────────
  // Question IDs for the exemptions survey
  const Q_EXEMPT_EMAIL  = '5117da3a-6e8a-4dcd-abbd-4a59277231aa'; // "Student's email address (King's email)"
  const Q_EXEMPT_REASON = '1cd56cc3-734c-4618-afc6-8e7b0d64998c'; // "Reason for exemption request"

  const ingestExemptions = async () => {
    if (!exemptSurveyId.trim()) return flash('warn', 'Select or enter an exemptions survey ID first.');
    setIngestingExempt(true);
    try {
      await setDoc(doc(db, 'cocoSeasons', SEASON_ID), { exemptionsSurveyId: exemptSurveyId.trim() }, { merge: true });
      setConfig(c => ({ ...c, exemptionsSurveyId: exemptSurveyId.trim() }));

      const snap = await getDocs(collection(db, 'surveys', exemptSurveyId.trim(), 'responses'));
      if (snap.empty) { flash('warn', 'No responses found for that survey.'); setIngestingExempt(false); return; }

      // Each submission = one exemption request. Email comes from the answer field, not respondentEmail.
      const exemptEmails = new Set(
        snap.docs.map(d => {
          const data = d.data();
          // Try the dedicated email question first, then fall back to respondentEmail
          const email = (data.answers?.[Q_EXEMPT_EMAIL] || data.respondentEmail || '').trim().toLowerCase();
          return email;
        }).filter(Boolean)
      );

      const batch = writeBatch(db);
      const updated = { ...roster };
      let matched = 0;
      let unmatched = [];

      for (const [id, student] of Object.entries(updated)) {
        const studentEmail = (student.email || '').toLowerCase();
        if (exemptEmails.has(studentEmail)) {
          // Also store the exemption reason if available
          const responseDoc = snap.docs.find(d => {
            const e = (d.data().answers?.[Q_EXEMPT_EMAIL] || d.data().respondentEmail || '').trim().toLowerCase();
            return e === studentEmail;
          });
          const reason = responseDoc?.data().answers?.[Q_EXEMPT_REASON] || '';
          batch.update(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), {
            status: 'exempt',
            exemptionReason: reason,
          });
          updated[id] = { ...student, status: 'exempt', exemptionReason: reason };
          matched++;
        }
      }

      // Track emails that submitted exemptions but weren't found in the roster
      exemptEmails.forEach(email => {
        const found = Object.values(updated).some(s => (s.email || '').toLowerCase() === email);
        if (!found) unmatched.push(email);
      });

      await batch.commit();
      setRoster(updated);

      let msg = `Marked ${matched} of ${exemptEmails.size} submitters as exempt.`;
      if (unmatched.length > 0) msg += ` ${unmatched.length} submitter(s) not found in roster: ${unmatched.join(', ')}`;
      flash(matched > 0 ? 'success' : 'warn', msg);
    } catch (err) {
      flash('warn', 'Exemption ingest failed: ' + err.message);
    }
    setIngestingExempt(false);
  };

  // ── Add team sport players from SIS ID list ─────────────────────────────
  const addTeamSportPlayers = async () => {
    // Accept any whitespace, commas, or newlines as separators
    const sisIds = csvText.split(/[\s,]+/).map(v => v.trim()).filter(Boolean);
    if (!sisIds.length) return flash('warn', 'No SIS IDs found — paste them in the box below.');

    const batch = writeBatch(db);
    const updated = { ...roster };
    let matched = 0;
    const notFound = [];

    for (const sisId of sisIds) {
      // Match by sisId field (key) or by sisId stored on the doc
      const entry = updated[sisId]
        ? [sisId, updated[sisId]]
        : Object.entries(updated).find(([, s]) => s.sisId === sisId);

      if (entry) {
        const [id, student] = entry;
        const newStatus = student.status === 'active' ? 'offseason' : student.status;
        batch.update(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), {
          status: newStatus, teamSport,
        });
        updated[id] = { ...student, status: newStatus, teamSport };
        matched++;
      } else {
        notFound.push(sisId);
      }
    }

    await batch.commit();
    setRoster(updated);
    setCsvText('');

    let msg = `Marked ${matched} of ${sisIds.length} students as ${teamSport} (offseason).`;
    if (notFound.length) msg += ` Not found in roster: ${notFound.join(', ')}`;
    flash(matched > 0 ? 'success' : 'warn', msg);
  };

  // ── Manual exemptions ───────────────────────────────────────────────────
  // Accepts email addresses or SIS IDs, one per line / comma / space separated.
  const addManualExemptions = async () => {
    const raw = manualExemptText.trim();
    if (!raw) return flash('warn', 'Paste at least one email or SIS ID.');
    const tokens = raw.split(/[\s,\n]+/).map(t => t.trim().toLowerCase()).filter(Boolean);
    if (!tokens.length) return flash('warn', 'No valid entries found.');
    setManualExempting(true);

    // Build lookup maps
    const byEmail = {}; // email → rosterId
    const bySisId = {}; // sisId → rosterId
    Object.entries(roster).forEach(([rid, s]) => {
      if (s.email)  byEmail[(s.email || '').toLowerCase()] = rid;
      if (s.emailLower) byEmail[s.emailLower] = rid;
      if (s.sisId)  bySisId[String(s.sisId).trim()] = rid;
    });

    const batch = writeBatch(db);
    const updated = { ...roster };
    const matched = [], notFound = [];

    tokens.forEach(token => {
      // Try email first, then sisId
      const rid = byEmail[token] || bySisId[token];
      if (rid) {
        matched.push(rid);
        batch.set(doc(db, 'cocoSeasons', SEASON_ID, 'roster', rid), {
          status: 'exempt',
          exemptionReason: manualExemptReason.trim() || 'Manual exemption',
        }, { merge: true });
        updated[rid] = { ...updated[rid], status: 'exempt', exemptionReason: manualExemptReason.trim() || 'Manual exemption' };
      } else {
        notFound.push(token);
      }
    });

    if (matched.length) await batch.commit();
    setRoster(updated);
    setManualExemptText('');
    setManualExemptNotFound(notFound);

    let msg = `Exempted ${matched.length} of ${tokens.length} students.`;
    if (notFound.length) msg += ` ${notFound.length} not found — see list below.`;
    flash(matched.length > 0 ? 'success' : 'warn', msg);
    setManualExempting(false);
  };

  // ── Manual status toggle ────────────────────────────────────────────────
  const toggleStatus = async (id, newStatus) => {
    await updateDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), { status: newStatus });
    setRoster(r => ({ ...r, [id]: { ...r[id], status: newStatus } }));
  };

  const removeStudent = async (id, name) => {
    if (!window.confirm(`Remove ${name || id} from the roster?`)) return;
    await deleteDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id));
    setRoster(r => { const next = { ...r }; delete next[id]; return next; });
  };

  // ── Filtered + sorted roster ─────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SORT_GETTERS = {
    priority: s => (s.priority ? '0' : '1'), // priority students sort first
    name:     s => (s.name || '').toLowerCase(),
    grade:    s => s.grade || '',
    cohort:   s => s.cohort || '',
    sisId:    s => s.sisId || '',
    status:   s => s.status || '',
    choice1:  s => s.choice1 || '',
    choice2:  s => s.choice2 || '',
    choice3:  s => s.choice3 || '',
    teamSport:s => s.teamSport || '',
  };

  const filtered = rosterList
    .filter(s => {
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      if (filterGrade !== 'all' && s.grade !== filterGrade) return false;
      if (search && !s.name?.toLowerCase().includes(search.toLowerCase()) && !s.email?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const getter = SORT_GETTERS[sortCol] || SORT_GETTERS.name;
      const av = getter(a), bv = getter(b);
      const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });

  return (
    <div>
      {/* Load all students */}
      <div style={s.card}>
        <div style={s.sectionTitle}>1. Load All Students</div>
        <p style={{ fontSize: 13, color: 'var(--ka-gray-500)', marginBottom: 14 }}>
          Pull every student from the school directory into the roster. Students who have already submitted the coco survey keep their status and choices — only new students are added, defaulting to <strong>Active</strong>.
        </p>
        <div style={s.row}>
          <button style={s.btn()} onClick={loadAllStudents} disabled={loadingAllStudents}>
            {loadingAllStudents ? 'Loading…' : '👥 Load All Students'}
          </button>
          {rosterList.length > 0 && (
            <>
              <span style={{ fontSize: 13, color: 'var(--ka-gray-500)' }}>
                {rosterList.length} students in roster
              </span>
              {(() => {
                const badCount = rosterList.filter(s =>
                  !UPPER_SCHOOL_GRADES.includes(s.grade) || !s.grade || s.active === false || s.status === 'inactive'
                ).length;
                return (
                  <button style={s.btn('danger')} onClick={purgeNonUpperSchool} disabled={purging}>
                    {purging ? 'Removing…' : badCount > 0 ? `🗑 Purge Ineligible Students (${badCount})` : '🗑 Purge Ineligible Students'}
                  </button>
                );
              })()}
              <button
                style={{ ...s.btn('danger'), background: '#7f1d1d' }}
                onClick={clearRoster}
                disabled={clearing || rosterList.length === 0}
                title="Delete every student from the roster so you can re-ingest from scratch"
              >
                {clearing ? 'Clearing…' : `☠ Clear Entire Roster (${rosterList.length})`}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Survey Ingestion */}
      <div style={s.card}>
        {/* Load survey list */}
        {!surveysLoaded && (
          <div style={{ marginBottom: 16 }}>
            <button style={s.btn('ghost')} onClick={loadSurveys} disabled={loadingSurveys}>
              {loadingSurveys ? 'Loading surveys…' : '🔍 Browse Available Surveys'}
            </button>
          </div>
        )}

        {surveysLoaded && availableSurveys.length > 0 && (
          <div style={{ marginBottom: 20, padding: '12px 14px', background: 'var(--ka-gray-50)', borderRadius: 8, border: '1px solid var(--ka-gray-200)' }}>
            <span style={s.label}>Available Surveys ({availableSurveys.length})</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto', marginTop: 4 }}>
              {availableSurveys.map(sv => (
                <div key={sv.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                  background: 'var(--ka-white)', borderRadius: 6, border: '1px solid var(--ka-gray-200)',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{sv.title || sv.name || '(Untitled)'}</div>
                    <div style={{ fontSize: 11, color: 'var(--ka-gray-500)' }}>
                      ID: {sv.id}
                      {sv.responseCount != null && ` · ${sv.responseCount} responses`}
                      {sv.createdAt && ` · ${new Date(sv.createdAt?.seconds ? sv.createdAt.seconds * 1000 : sv.createdAt).toLocaleDateString()}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      style={{ ...s.btn('ghost'), fontSize: 11, padding: '4px 10px' }}
                      onClick={() => setSurveyId(sv.id)}
                    >
                      Use as Main
                    </button>
                    <button
                      style={{ ...s.btn('ghost'), fontSize: 11, padding: '4px 10px' }}
                      onClick={() => setExemptSurveyId(sv.id)}
                    >
                      Use as Exemptions
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {surveysLoaded && availableSurveys.length === 0 && (
          <div style={{ ...s.warn, marginBottom: 16 }}>No surveys found in Firestore.</div>
        )}

        <div style={s.sectionTitle}>2. Load Main Coco Survey (Optional — overlays choices onto roster)</div>
        <div style={{ ...s.row, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <span style={s.label}>Selected Survey ID</span>
            <input
              style={{ ...s.input, width: '100%', maxWidth: 400, boxSizing: 'border-box',
                background: surveyId ? '#f0fdf4' : undefined,
                borderColor: surveyId ? '#86efac' : undefined,
              }}
              value={surveyId}
              onChange={e => setSurveyId(e.target.value)}
              placeholder="Select from list above, or paste ID manually"
            />
          </div>
          <div style={{ marginTop: 16 }}>
            <button style={s.btn()} onClick={ingestMainSurvey} disabled={ingestingMain || !surveyId.trim()}>
              {ingestingMain ? 'Loading…' : rosterList.length > 0 ? '↻ Re-Ingest' : 'Ingest Responses'}
            </button>
          </div>
        </div>

        <div style={s.sectionTitle}>3. Load Exemptions Survey</div>
        <div style={s.row}>
          <div style={{ flex: 1 }}>
            <span style={s.label}>Selected Survey ID</span>
            <input
              style={{ ...s.input, width: '100%', maxWidth: 400, boxSizing: 'border-box',
                background: exemptSurveyId ? '#f0fdf4' : undefined,
                borderColor: exemptSurveyId ? '#86efac' : undefined,
              }}
              value={exemptSurveyId}
              onChange={e => setExemptSurveyId(e.target.value)}
              placeholder="Select from list above, or paste ID manually"
            />
          </div>
          <div style={{ marginTop: 16 }}>
            <button style={s.btn()} onClick={ingestExemptions} disabled={ingestingExempt || !exemptSurveyId.trim()}>
              {ingestingExempt ? 'Loading…' : 'Apply Exemptions'}
            </button>
          </div>
        </div>
      </div>

      {/* Add team sport players */}
      <div style={s.card}>
        <div style={s.sectionTitle}>4. Add Soccer / Volleyball Players</div>
        <p style={{ fontSize: 13, color: 'var(--ka-gray-500)', marginBottom: 12 }}>
          Paste a list of SIS IDs — one per line, comma-separated, or space-separated — to mark those students as offseason training.
        </p>
        <div style={{ ...s.row, alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <span style={s.label}>Sport</span>
            <select style={s.select} value={teamSport} onChange={e => setTeamSport(e.target.value)}>
              <option>Volleyball</option>
              <option>Football</option>
              <option>Both Volleyball and Football</option>
            </select>
          </div>
        </div>
        <textarea
          style={{ ...s.input, width: '100%', height: 80, resize: 'vertical', marginBottom: 8, boxSizing: 'border-box' }}
          placeholder="166985, 167234, 168001 …  (paste any number of SIS IDs)"
          value={csvText} onChange={e => setCsvText(e.target.value)}
        />
        <button style={s.btn()} onClick={addTeamSportPlayers}>Mark as Offseason</button>
      </div>

      {/* Manual exemptions */}
      <div style={s.card}>
        <div style={s.sectionTitle}>5. Manual Exemptions</div>
        <p style={{ fontSize: 13, color: 'var(--ka-gray-500)', marginBottom: 12 }}>
          Paste student emails or SIS IDs (one per line, or comma/space-separated) to mark them as exempt.
          Accepts mixed lists — email and SIS ID can appear together.
        </p>
        <div style={{ marginBottom: 10 }}>
          <span style={s.label}>Reason (optional — applied to all entries in this batch)</span>
          <input
            style={{ ...s.input, width: '100%', maxWidth: 400, boxSizing: 'border-box' }}
            placeholder="e.g. Medical, Family, Study hall…"
            value={manualExemptReason}
            onChange={e => setManualExemptReason(e.target.value)}
          />
        </div>
        <textarea
          style={{ ...s.input, width: '100%', height: 100, resize: 'vertical', marginBottom: 8, boxSizing: 'border-box' }}
          placeholder={'student@kings.edu.jo\n167234\nstudent2@kings.edu.jo, 168001'}
          value={manualExemptText}
          onChange={e => { setManualExemptText(e.target.value); setManualExemptNotFound([]); }}
        />
        <button style={s.btn('danger')} onClick={addManualExemptions} disabled={manualExempting || !manualExemptText.trim()}>
          {manualExempting ? 'Exempting…' : '⊘ Mark as Exempt'}
        </button>

        {manualExemptNotFound.length > 0 && (
          <div style={{ marginTop: 16, padding: 14, background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: '#854d0e' }}>
                ⚠ {manualExemptNotFound.length} entr{manualExemptNotFound.length === 1 ? 'y' : 'ies'} not found in roster
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={{ ...s.btn('ghost'), fontSize: 12, padding: '3px 10px' }}
                  onClick={() => {
                    navigator.clipboard.writeText(manualExemptNotFound.join('\n'));
                    flash('success', 'Copied to clipboard.');
                  }}
                >
                  Copy list
                </button>
                <button
                  style={{ ...s.btn('ghost'), fontSize: 12, padding: '3px 10px' }}
                  onClick={() => setManualExemptNotFound([])}
                >
                  Dismiss
                </button>
              </div>
            </div>
            <div style={{
              fontFamily: 'monospace', fontSize: 12, color: '#713f12',
              maxHeight: 200, overflowY: 'auto',
              background: '#fefce8', borderRadius: 6, padding: '8px 10px',
              lineHeight: 1.7,
            }}>
              {manualExemptNotFound.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </div>
            <p style={{ fontSize: 12, color: '#854d0e', marginTop: 8, marginBottom: 0 }}>
              These may be misspelled, use a personal email instead of King's email, or simply not be in the roster yet.
            </p>
          </div>
        )}
      </div>

      {/* Stats */}
      {rosterList.length > 0 && (
        <div style={{ ...s.card, padding: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={s.stat}><div style={s.statN}>{rosterList.length}</div><div style={s.statL}>Total</div></div>
            {Object.entries(STATUS_LABELS).map(([key, { label, color }]) => {
              const n = rosterList.filter(r => r.status === key).length;
              return n > 0 ? (
                <div key={key} style={s.stat}>
                  <div style={{ ...s.statN, color }}>{n}</div>
                  <div style={s.statL}>{label}</div>
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}

      {/* Roster table */}
      {rosterList.length > 0 && (
        <div style={s.card}>
          <div style={{ ...s.row, marginBottom: 12 }}>
            <input style={{ ...s.input, width: 200 }} placeholder="Search name / email…" value={search} onChange={e => setSearch(e.target.value)} />
            <select style={s.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">All statuses</option>
              {Object.entries(STATUS_LABELS).map(([k, { label }]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <select style={s.select} value={filterGrade} onChange={e => setFilterGrade(e.target.value)}>
              <option value="all">All grades</option>
              {['9', '10', '11', '12'].map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--ka-gray-500)', marginLeft: 'auto' }}>{filtered.length} shown</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  {[
                    ['priority',  '⭐'],
                    ['name',      'Name'],
                    ['grade',     'Grade'],
                    ['cohort',    'Cohort'],
                    ['sisId',     'SIS ID'],
                    ['status',    'Status'],
                    ['choice1',   '1st'],
                    ['choice2',   '2nd'],
                    ['choice3',   '3rd'],
                    ['teamSport', 'Team Sport'],
                  ].map(([col, label]) => (
                    <th
                      key={col}
                      style={{ ...s.th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort(col)}
                    >
                      {label}
                      {sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
                    </th>
                  ))}
                  <th style={s.th}>Exemption Reason</th>
                  {canManage && <th style={s.th}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(student => {
                  const info = STATUS_LABELS[student.status] || { label: student.status, color: '#6b7280' };
                  return (
                    <tr key={student.id} style={student.priority ? { background: '#fffbeb' } : {}}>
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        {canManage ? (
                          <span
                            title={student.priority ? 'Priority — click to remove' : 'Click to mark priority'}
                            style={{ cursor: 'pointer', fontSize: 15, opacity: student.priority ? 1 : 0.2 }}
                            onClick={async () => {
                              const newVal = !student.priority;
                              await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', student.id), { priority: newVal }, { merge: true });
                              setRoster(r => ({ ...r, [student.id]: { ...r[student.id], priority: newVal } }));
                            }}
                          >⭐</span>
                        ) : (student.priority ? '⭐' : '')}
                      </td>
                      <td style={s.td}>
                        <div style={{ fontWeight: 500 }}>{student.name || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--ka-gray-500)' }}>{student.email}</div>
                      </td>
                      <td style={s.td}>{student.grade || '—'}</td>
                      <td style={s.td}>
                        {student.cohort ? (
                          <span style={s.badge(student.cohort === 'Day' ? '#059669' : '#7c3aed')}>
                            {student.cohort}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={s.td}>{student.sisId || <span style={{ color: '#dc2626' }}>Missing</span>}</td>
                      <td style={s.td}>
                        <span style={s.badge(info.color)}>{info.label}</span>
                        {!student.responseId && (
                          <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--ka-gray-400)' }}>no survey</span>
                        )}
                      </td>
                      {(() => {
                        const dc = dedupeChoices(student.choice1, student.choice2, student.choice3);
                        return (<>
                          <td style={s.td}>{dc.choice1 || '—'}</td>
                          <td style={s.td}>{dc.choice2 || <span style={{ color: 'var(--ka-gray-300)' }}>—</span>}</td>
                          <td style={s.td}>{dc.choice3 || <span style={{ color: 'var(--ka-gray-300)' }}>—</span>}</td>
                        </>);
                      })()}
                      <td style={s.td}>{student.teamSport || '—'}</td>
                      <td style={{ ...s.td, fontSize: 12, color: 'var(--ka-gray-500)', maxWidth: 180 }}>
                        {student.exemptionReason || '—'}
                      </td>
                      {canManage && (
                        <td style={s.td}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <select
                              style={{ ...s.select, fontSize: 11, padding: '3px 6px' }}
                              value={student.status}
                              onChange={e => toggleStatus(student.id, e.target.value)}
                            >
                              {Object.entries(STATUS_LABELS).map(([k, { label }]) =>
                                <option key={k} value={k}>{label}</option>
                              )}
                            </select>
                            <button
                              title="Remove from roster"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: 'var(--ka-gray-400)', lineHeight: 1, padding: '2px 4px' }}
                              onClick={() => removeStudent(student.id, student.name)}
                            >🗑</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 2 — Activities & Staff
// ═══════════════════════════════════════════════════════════════════════════
function ActivitiesTab({ activities, activityList, setActivities, roster, setRoster, canManage, flash, saving, setSaving }) {
  const [editing, setEditing] = useState(null);       // docId with staff panel open
  const [bulkOpen, setBulkOpen] = useState(null);     // docId with bulk-assign panel open
  const [bulkPasteText, setBulkPasteText] = useState('');
  const [bulkPasting, setBulkPasting] = useState(false);
  // Individual assign (name search)
  const [assignSearch, setAssignSearch] = useState('');
  const [assignSuggestions, setAssignSuggestions] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);

  const [priorityOpen, setPriorityOpen] = useState(null); // docId with priority panel open
  const [priorityText, setPriorityText] = useState('');
  const [prioritySaving, setPrioritySaving] = useState(false);
  // Priority name search
  const [prioritySearch, setPrioritySearch] = useState('');
  const [prioritySuggestions, setPrioritySuggestions] = useState([]);

  const [ineligibleOpen, setIneligibleOpen] = useState(null); // docId with ineligible panel open
  const [ineligibleText, setIneligibleText] = useState('');
  const [ineligibleSaving, setIneligibleSaving] = useState(false);
  // Ineligible name search
  const [ineligibleSearch, setIneligibleSearch] = useState('');
  const [ineligibleSuggestions, setIneligibleSuggestions] = useState([]);
  const [staffName, setStaffName] = useState('');
  const [staffType, setStaffType] = useState('teacher'); // 'teacher' | 'coach'
  const [staffSuggestions, setStaffSuggestions] = useState([]);
  const [allStaff, setAllStaff] = useState([]); // loaded from Firestore /staff
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [renamingDocId, setRenamingDocId] = useState(null);  // docId being renamed
  const [renameValue, setRenameValue] = useState('');         // current input value
  const [renameSaving, setRenameSaving] = useState(false);
  const [newActivityName, setNewActivityName] = useState('');
  const [newActivityCapacity, setNewActivityCapacity] = useState(20);
  const [addingActivity, setAddingActivity] = useState(false);

  // Load staff collection once when the tab mounts
  useEffect(() => {
    if (staffLoaded) return;
    getDocs(collection(db, 'staff')).then(snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllStaff(list);
      setStaffLoaded(true);
    }).catch(() => setStaffLoaded(true));
  }, [staffLoaded]);

  // Filter suggestions as the user types (teacher mode only)
  const handleStaffNameChange = (val) => {
    setStaffName(val);
    if (staffType === 'teacher' && val.trim().length > 0) {
      const q = val.toLowerCase();
      setStaffSuggestions(
        allStaff
          .filter(m => (m.name || m.displayName || '').toLowerCase().includes(q))
          .slice(0, 6)
      );
    } else {
      setStaffSuggestions([]);
    }
  };

  const pickSuggestion = (member) => {
    setStaffName(member.name || member.displayName || '');
    setStaffSuggestions([]);
  };

  const saveCapacity = async (docId, capacity) => {
    setSaving(true);
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', docId),
      { capacity: Number(capacity) }, { merge: true });
    setActivities(a => ({ ...a, [docId]: { ...a[docId], capacity: Number(capacity) } }));
    setSaving(false);
  };

  // Rename an activity: updates name on this doc only. All sessions sharing the same
  // base name but different docIds keep their own names — rename each session separately
  // if needed. Optionally updates placed field on roster docs referencing the old name.
  const saveRename = async (docId) => {
    const newName = renameValue.trim();
    if (!newName) return;
    const oldName = actName(docId, activities[docId]);
    if (newName === oldName) { setRenamingDocId(null); return; }
    setRenameSaving(true);
    try {
      // 1. Update the activity doc name
      await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', docId), { name: newName }, { merge: true });
      setActivities(a => ({ ...a, [docId]: { ...a[docId], name: newName } }));

      // 2. Update roster docs where placed === oldName AND placedDocId === docId
      const toUpdate = Object.entries(roster).filter(
        ([, s]) => s.placed === oldName && s.placedDocId === docId
      );
      if (toUpdate.length > 0) {
        const batch = writeBatch(db);
        toUpdate.forEach(([rid]) => {
          batch.set(doc(db, 'cocoSeasons', SEASON_ID, 'roster', rid), { placed: newName }, { merge: true });
        });
        await batch.commit();
        setRoster(r => {
          const next = { ...r };
          toUpdate.forEach(([rid]) => { next[rid] = { ...next[rid], placed: newName }; });
          return next;
        });
      }

      flash('success', `Renamed "${oldName}" → "${newName}"${toUpdate.length ? ` and updated ${toUpdate.length} placements.` : '.'}`);
      setRenamingDocId(null);
    } catch (err) {
      flash('warn', 'Rename failed: ' + err.message);
    }
    setRenameSaving(false);
  };

  const addStaff = async (docId) => {
    if (!staffName.trim()) return;
    const member = {
      name: staffName.trim(),
      type: staffType, // 'teacher' | 'coach'
    };
    const updated = [...(activities[docId]?.staff || []), member];
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', docId),
      { staff: updated }, { merge: true });
    setActivities(a => ({ ...a, [docId]: { ...a[docId], staff: updated } }));
    setStaffName('');
    setStaffSuggestions([]);
    flash('success', `Added ${member.name} to ${activities[docId]?.name}.`);
  };

  const removeStaff = async (docId, idx) => {
    const updated = activities[docId].staff.filter((_, i) => i !== idx);
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', docId), { staff: updated }, { merge: true });
    setActivities(a => ({ ...a, [docId]: { ...a[docId], staff: updated } }));
  };

  // Duplicate an activity — adds a new session for the same base name
  const duplicateActivity = async (docId) => {
    const src = activities[docId];
    if (!src) return;
    // Find max session number for this base name
    const existingSessions = Object.values(activities)
      .filter(a => a.name === src.name)
      .map(a => a.session);
    const nextSession = Math.max(...existingSessions) + 1;
    const newDocId = toDocId(src.name, nextSession);
    const newActivity = { name: src.name, session: nextSession, capacity: src.capacity, staff: [] };
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', newDocId), newActivity);
    setActivities(a => ({ ...a, [newDocId]: newActivity }));
    flash('success', `Added Session ${nextSession} for ${src.name}.`);
  };

  // Add a brand-new activity (session 1)
  const addActivity = async () => {
    console.log('[addActivity] called, newActivityName=', newActivityName);
    const name = newActivityName.trim();
    if (!name) return flash('warn', 'Enter an activity name.');
    const docId = toDocId(name, 1);
    if (activities[docId]) return flash('warn', `"${name}" already exists.`);
    setAddingActivity(true);
    const newAct = { name, session: 1, capacity: Number(newActivityCapacity) || 20, staff: [] };
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', docId), newAct);
    setActivities(a => ({ ...a, [docId]: newAct }));
    setNewActivityName('');
    setNewActivityCapacity(20);
    setAddingActivity(false);
    flash('success', `Added "${name}".`);
  };

  // Remove a specific session (session > 1 only via this path)
  const removeSession = async (docId) => {
    const act = activities[docId];
    if (!act || act.session === 1) return flash('warn', 'Cannot remove the primary session.');
    if (!window.confirm(`Remove ${actName(docId, act)} — Session ${act.session}? This cannot be undone.`)) return;
    await deleteDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', docId));
    setActivities(a => { const next = { ...a }; delete next[docId]; return next; });
    flash('success', `Removed Session ${act.session} of ${actName(docId, act)}.`);
  };

  // Bulk-assign students to an activity by pasting SIS IDs
  const bulkAssign = async (docId) => {
    if (!roster) return flash('warn', 'Roster not loaded yet.');
    const raw = bulkPasteText.trim();
    if (!raw) return;
    // Parse: split on any whitespace or commas, strip blanks
    const sisIds = raw.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    if (sisIds.length === 0) return flash('warn', 'No SIS IDs found.');

    setBulkPasting(true);
    // Build lookup: sisId → roster doc id
    const byId = {};
    Object.entries(roster).forEach(([rosterId, s]) => {
      if (s.sisId) byId[String(s.sisId).trim()] = rosterId;
    });

    const matched = [];
    const unmatched = [];
    sisIds.forEach(sid => {
      const rosterId = byId[sid];
      if (rosterId) matched.push(rosterId);
      else unmatched.push(sid);
    });

    if (matched.length === 0) {
      flash('warn', `No roster matches for ${unmatched.length} SIS ID(s).`);
      setBulkPasting(false);
      return;
    }

    const act = activities[docId];
    const actDisplayName = actName(docId, act);

    // Batch write placements
    try {
      const chunks = [];
      for (let i = 0; i < matched.length; i += 500) chunks.push(matched.slice(i, i + 500));
      for (const chunk of chunks) {
        const b = writeBatch(db);
        chunk.forEach(rosterId => {
          b.set(doc(db, 'cocoSeasons', SEASON_ID, 'roster', rosterId), {
            placed: actDisplayName,
            placedDocId: docId,
            placedRound: 0, // 0 = manual / bulk assign
            status: 'active',
            manualOverride: true,
          }, { merge: true });
        });
        await b.commit();
      }
      // Update local state
      setRoster(r => {
        const next = { ...r };
        matched.forEach(rosterId => {
          next[rosterId] = {
            ...next[rosterId],
            placed: actDisplayName,
            placedDocId: docId,
            placedRound: 0,
            status: 'active',
            manualOverride: true,
          };
        });
        return next;
      });
      const msg = `Assigned ${matched.length} student${matched.length !== 1 ? 's' : ''} to ${actDisplayName}.` +
        (unmatched.length > 0 ? ` ${unmatched.length} SIS ID(s) not found: ${unmatched.join(', ')}` : '');
      flash(unmatched.length > 0 ? 'warn' : 'success', msg);
      setBulkPasteText('');
      setBulkOpen(null);
    } catch (err) {
      flash('warn', 'Bulk assign failed: ' + err.message);
    }
    setBulkPasting(false);
  };

  // Helper: close all expandable panels on a card when opening a different one
  const closeAllPanels = () => {
    setBulkOpen(null); setBulkPasteText('');
    setAssignSearch(''); setAssignSuggestions([]);
    setPriorityOpen(null); setPriorityText('');
    setPrioritySearch(''); setPrioritySuggestions([]);
    setIneligibleOpen(null); setIneligibleText('');
    setIneligibleSearch(''); setIneligibleSuggestions([]);
    setEditing(null);
    setRenamingDocId(null);
  };

  // ── Name-search helpers ──────────────────────────────────────────────────
  // Returns roster entries whose name includes the query (case-insensitive)
  const searchRosterByName = (q) => {
    if (!q || !q.trim()) return [];
    const lq = q.toLowerCase();
    return Object.entries(roster)
      .filter(([, s]) => (s.name || '').toLowerCase().includes(lq))
      .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || ''))
      .slice(0, 8);
  };

  // Assign a single student to an activity immediately
  const assignOne = async (docId, rosterId) => {
    const act = activities[docId];
    const actDisplayName = actName(docId, act);
    setAssignSaving(true);
    try {
      await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', rosterId), {
        placed: actDisplayName, placedDocId: docId, placedRound: 0,
        status: 'active', manualOverride: true,
      }, { merge: true });
      setRoster(r => ({
        ...r,
        [rosterId]: { ...r[rosterId], placed: actDisplayName, placedDocId: docId, placedRound: 0, status: 'active', manualOverride: true },
      }));
      flash('success', `Assigned ${roster[rosterId]?.name || rosterId} to ${actDisplayName}.`);
      setAssignSearch(''); setAssignSuggestions([]);
    } catch (err) { flash('warn', 'Assign failed: ' + err.message); }
    setAssignSaving(false);
  };

  // Mark a single student as priority
  const markPriorityOne = async (rosterId) => {
    try {
      await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', rosterId), { priority: true }, { merge: true });
      setRoster(r => ({ ...r, [rosterId]: { ...r[rosterId], priority: true } }));
      flash('success', `${roster[rosterId]?.name || rosterId} marked as priority.`);
      setPrioritySearch(''); setPrioritySuggestions([]);
    } catch (err) { flash('warn', 'Priority save failed: ' + err.message); }
  };

  // Mark a single student as ineligible for an activity
  const markIneligibleOne = async (docId, rosterId) => {
    const student = roster[rosterId];
    if (!student?.sisId) return flash('warn', 'Student has no SIS ID.');
    const sid = String(student.sisId);
    const existing = activities[docId]?.ineligible || [];
    if (existing.includes(sid)) { flash('warn', `${student.name} is already ineligible for this activity.`); return; }
    const merged = [...existing, sid];
    try {
      await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', docId), { ineligible: merged }, { merge: true });
      setActivities(a => ({ ...a, [docId]: { ...a[docId], ineligible: merged } }));
      flash('success', `${student.name} marked ineligible for ${actName(docId, activities[docId])}.`);
      setIneligibleSearch(''); setIneligibleSuggestions([]);
    } catch (err) { flash('warn', 'Ineligible save failed: ' + err.message); }
  };

  // Bulk-mark students as priority (priority:true on roster doc)
  const bulkPriority = async () => {
    if (!roster) return flash('warn', 'Roster not loaded yet.');
    const raw = priorityText.trim();
    if (!raw) return;
    const sisIds = raw.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    if (sisIds.length === 0) return flash('warn', 'No SIS IDs found.');
    setPrioritySaving(true);
    const byId = {};
    Object.entries(roster).forEach(([rosterId, s]) => {
      if (s.sisId) byId[String(s.sisId).trim()] = rosterId;
    });
    const matched = [], unmatched = [];
    sisIds.forEach(sid => {
      const rid = byId[sid];
      if (rid) matched.push(rid); else unmatched.push(sid);
    });
    if (matched.length === 0) {
      flash('warn', `No roster matches for ${unmatched.length} SIS ID(s).`);
      setPrioritySaving(false);
      return;
    }
    try {
      const chunks = [];
      for (let i = 0; i < matched.length; i += 500) chunks.push(matched.slice(i, i + 500));
      for (const chunk of chunks) {
        const b = writeBatch(db);
        chunk.forEach(rid => b.set(doc(db, 'cocoSeasons', SEASON_ID, 'roster', rid), { priority: true }, { merge: true }));
        await b.commit();
      }
      setRoster(r => {
        const next = { ...r };
        matched.forEach(rid => { next[rid] = { ...next[rid], priority: true }; });
        return next;
      });
      const msg = `Marked ${matched.length} student${matched.length !== 1 ? 's' : ''} as priority.` +
        (unmatched.length > 0 ? ` ${unmatched.length} not found: ${unmatched.join(', ')}` : '');
      flash(unmatched.length > 0 ? 'warn' : 'success', msg);
      setPriorityText('');
      setPriorityOpen(null);
    } catch (err) { flash('warn', 'Priority save failed: ' + err.message); }
    setPrioritySaving(false);
  };

  // Bulk-mark students as ineligible for a specific activity (stored on the activity doc)
  const bulkIneligible = async (docId) => {
    const raw = ineligibleText.trim();
    if (!raw) return;
    const sisIds = [...new Set(raw.split(/[\s,]+/).map(s => s.trim()).filter(Boolean))];
    if (sisIds.length === 0) return flash('warn', 'No SIS IDs found.');
    setIneligibleSaving(true);
    try {
      const existing = activities[docId]?.ineligible || [];
      const merged = [...new Set([...existing, ...sisIds])];
      await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', docId), { ineligible: merged }, { merge: true });
      setActivities(a => ({ ...a, [docId]: { ...a[docId], ineligible: merged } }));
      flash('success', `${sisIds.length} SIS ID(s) marked ineligible for ${actName(docId, activities[docId])}.`);
      setIneligibleText('');
      setIneligibleOpen(null);
    } catch (err) { flash('warn', 'Ineligible save failed: ' + err.message); }
    setIneligibleSaving(false);
  };

  // Remove one SIS ID from the ineligible list for an activity
  const removeIneligible = async (docId, sisId) => {
    const updated = (activities[docId]?.ineligible || []).filter(s => s !== sisId);
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', docId), { ineligible: updated }, { merge: true });
    setActivities(a => ({ ...a, [docId]: { ...a[docId], ineligible: updated } }));
  };

  // Remove an entire activity (all sessions)
  const removeActivity = async (docId, act) => {
    if (!window.confirm(`Remove "${actName(docId, act)}" entirely? This cannot be undone.`)) return;
    // Delete all sessions of this activity
    const toDelete = Object.entries(activities).filter(([, a]) => a.name === act.name).map(([id]) => id);
    for (const id of toDelete) {
      await deleteDoc(doc(db, 'cocoSeasons', SEASON_ID, 'activities', id));
    }
    setActivities(a => {
      const next = { ...a };
      toDelete.forEach(id => delete next[id]);
      return next;
    });
    flash('success', `Removed "${actName(docId, act)}".`);
  };

  const totalCapacity = Object.values(activities).reduce((sum, a) => sum + (a.capacity || 0), 0);

  return (
    <div>
      {/* Add new activity */}
      {canManage && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Add Activity</div>
          <div style={s.row}>
            <div>
              <span style={s.label}>Activity Name</span>
              <input
                style={{ ...s.input, width: 240 }}
                value={newActivityName}
                onChange={e => setNewActivityName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addActivity()}
                placeholder="e.g. Archery"
              />
            </div>
            <div>
              <span style={s.label}>Capacity</span>
              <input
                type="number" min={1} max={200}
                style={{ ...s.input, width: 70 }}
                value={newActivityCapacity}
                onChange={e => setNewActivityCapacity(e.target.value)}
              />
            </div>
            <div style={{ marginTop: 16 }}>
              <button style={s.btn()} onClick={addActivity} disabled={addingActivity || !newActivityName.trim()}>
                {addingActivity ? 'Adding…' : '+ Add Activity'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ ...s.card, padding: 14, marginBottom: 16 }}>
        <div style={s.row}>
          <div style={s.stat}><div style={s.statN}>{activityList.length}</div><div style={s.statL}>Activity Sessions</div></div>
          <div style={s.stat}><div style={s.statN}>{totalCapacity}</div><div style={s.statL}>Total Capacity</div></div>
        </div>
      </div>

      {activityList.map(([docId, act]) => {
        const isOpen = editing === docId;
        const staff = act.staff || [];
        const hasMultipleSessions = Object.values(activities).filter(a => a.name === act.name).length > 1;
        return (
          <div key={docId} style={s.card}>
            <div style={{ ...s.row, marginBottom: isOpen ? 16 : 0 }}>
              {/* Name + session badge */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {renamingDocId === docId ? (
                  <>
                    <input
                      autoFocus
                      style={{ ...s.input, fontWeight: 600, fontSize: 14, minWidth: 180 }}
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveRename(docId);
                        if (e.key === 'Escape') setRenamingDocId(null);
                      }}
                      disabled={renameSaving}
                    />
                    <button style={s.btn('primary')} onClick={() => saveRename(docId)} disabled={renameSaving}>
                      {renameSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button style={s.btn('ghost')} onClick={() => setRenamingDocId(null)} disabled={renameSaving}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{actName(docId, act)}</span>
                    {canManage && (
                      <button
                        title="Rename activity"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ka-gray-400)', padding: '0 2px', lineHeight: 1 }}
                        onClick={() => { setRenamingDocId(docId); setRenameValue(actName(docId, act)); }}
                      >✏</button>
                    )}
                  </>
                )}
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 7px',
                  borderRadius: 20, background: (act.session || 1) === 1 ? '#dbeafe' : '#ede9fe',
                  color: (act.session || 1) === 1 ? '#1d4ed8' : '#7c3aed',
                }}>
                  Session {act.session || 1}
                </span>
              </div>

              {/* Capacity — controlled input so the value stays in sync */}
              <div style={s.row}>
                <span style={{ fontSize: 12, color: 'var(--ka-gray-500)' }}>Capacity:</span>
                <input
                  type="number" min={1} max={200}
                  style={{ ...s.input, width: 60 }}
                  value={act.capacity ?? 20}
                  onChange={e => {
                    const val = Number(e.target.value);
                    if (!isNaN(val) && val > 0) {
                      setActivities(a => ({ ...a, [docId]: { ...a[docId], capacity: val } }));
                    }
                  }}
                  onBlur={e => saveCapacity(docId, e.target.value)}
                />
              </div>

              {/* Staff badges */}
              {staff.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {staff.map((m, i) => (
                    <span key={i} style={s.badge(m.type === 'coach' ? '#059669' : '#2563eb')}>
                      {m.name}{m.type === 'coach' ? ' · Coach' : ' · Teacher'}
                      {canManage && <span style={{ cursor: 'pointer', marginLeft: 4 }} onClick={() => removeStaff(docId, i)}>×</span>}
                    </span>
                  ))}
                </div>
              )}

              {/* Action buttons */}
              {canManage && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    style={{ ...s.btn('ghost'), background: bulkOpen === docId ? '#dbeafe' : undefined, color: bulkOpen === docId ? '#1d4ed8' : undefined }}
                    onClick={() => { closeAllPanels(); if (bulkOpen !== docId) setBulkOpen(docId); }}
                  >
                    ⊕ Assign
                  </button>
                  <button
                    style={{ ...s.btn('ghost'), background: priorityOpen === docId ? '#fef3c7' : undefined, color: priorityOpen === docId ? '#92400e' : undefined }}
                    onClick={() => { closeAllPanels(); if (priorityOpen !== docId) setPriorityOpen(docId); }}
                  >
                    ⭐ Priority
                  </button>
                  <button
                    style={{ ...s.btn('ghost'), background: ineligibleOpen === docId ? '#fee2e2' : undefined, color: ineligibleOpen === docId ? '#991b1b' : undefined }}
                    onClick={() => { closeAllPanels(); if (ineligibleOpen !== docId) setIneligibleOpen(docId); }}
                  >
                    🚫 Ineligible{(act.ineligible?.length > 0) ? ` (${act.ineligible.length})` : ''}
                  </button>
                  <button style={s.btn('ghost')} onClick={() => { closeAllPanels(); if (!isOpen) setEditing(docId); }}>
                    + Staff
                  </button>
                  <button
                    title={`Add Session ${Object.values(activities).filter(a => a.name === act.name).length + 1} of ${actName(docId, act)}`}
                    style={{ ...s.btn('ghost'), fontSize: 16, padding: '6px 10px' }}
                    onClick={() => duplicateActivity(docId)}
                  >
                    ⧉
                  </button>
                  {(hasMultipleSessions && act.session > 1) || (!hasMultipleSessions) ? (
                    <button
                      title={hasMultipleSessions ? 'Remove this session' : 'Remove activity'}
                      style={{ ...s.btn('danger'), fontSize: 12, padding: '6px 10px' }}
                      onClick={() => hasMultipleSessions ? removeSession(docId) : removeActivity(docId, act)}
                    >
                      🗑
                    </button>
                  ) : null}
                </div>
              )}
            </div>

            {/* ── Assign panel ── */}
            {bulkOpen === docId && (
              <div style={{ borderTop: '1px solid var(--ka-gray-100)', paddingTop: 14 }}>
                <div style={s.sectionTitle}>⊕ Assign → {actName(docId, act)} (Session {act.session || 1})</div>

                {/* Individual name search */}
                <div style={{ marginBottom: 16 }}>
                  <span style={s.label}>Search by name</span>
                  <div style={{ position: 'relative', maxWidth: 320 }}>
                    <input
                      style={{ ...s.input, width: '100%', boxSizing: 'border-box' }}
                      value={assignSearch}
                      onChange={e => {
                        setAssignSearch(e.target.value);
                        setAssignSuggestions(searchRosterByName(e.target.value));
                      }}
                      placeholder="Type a student name…"
                      autoComplete="off"
                    />
                    {assignSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                        background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)',
                        borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        maxHeight: 220, overflowY: 'auto',
                      }}>
                        {assignSuggestions.map(([rid, st]) => (
                          <div
                            key={rid}
                            onClick={() => assignOne(docId, rid)}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--ka-gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--ka-gray-50)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span>
                              <span style={{ fontWeight: 500 }}>{st.name}</span>
                              <span style={{ fontSize: 11, color: 'var(--ka-gray-400)', marginLeft: 6 }}>
                                Grade {st.grade} · {st.sisId}
                              </span>
                            </span>
                            {st.placed && (
                              <span style={{ fontSize: 11, color: '#d97706' }}>→ {st.placed}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--ka-gray-400)', margin: '5px 0 0' }}>
                    Click a name to assign immediately. Currently placed students are shown in amber.
                  </p>
                </div>

                {/* Bulk SIS ID paste */}
                <div style={{ borderTop: '1px solid var(--ka-gray-100)', paddingTop: 12 }}>
                  <span style={s.label}>Bulk paste SIS IDs</span>
                  <p style={{ fontSize: 12, color: 'var(--ka-gray-500)', margin: '0 0 8px' }}>
                    One per line, or comma/space separated. Students will be placed here with <strong>manual override</strong>.
                  </p>
                  <textarea
                    style={{
                      width: '100%', minHeight: 90, padding: 10, fontSize: 13,
                      border: '1px solid var(--ka-gray-200)', borderRadius: 6,
                      fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical',
                      background: 'var(--ka-gray-50)',
                    }}
                    value={bulkPasteText}
                    onChange={e => setBulkPasteText(e.target.value)}
                    placeholder={'12345\n23456\n34567\n…'}
                  />
                  <div style={{ ...s.row, marginTop: 10 }}>
                    <button style={s.btn()} onClick={() => bulkAssign(docId)} disabled={bulkPasting || !bulkPasteText.trim()}>
                      {bulkPasting ? 'Assigning…' : `Assign ${bulkPasteText.trim() ? bulkPasteText.trim().split(/[\s,]+/).filter(Boolean).length : 0} Student(s)`}
                    </button>
                    <button style={s.btn('ghost')} onClick={() => { setBulkOpen(null); setBulkPasteText(''); setAssignSearch(''); setAssignSuggestions([]); }}>Close</button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Priority panel ── */}
            {priorityOpen === docId && (
              <div style={{ borderTop: '1px solid var(--ka-gray-100)', paddingTop: 14 }}>
                <div style={s.sectionTitle}>⭐ Mark as Priority</div>
                <p style={{ fontSize: 12, color: 'var(--ka-gray-500)', margin: '0 0 10px' }}>
                  Priority students are scheduled <strong>before all other students</strong> in every round, so they get their 1st choice first.
                </p>

                {/* Current priority list */}
                {(() => {
                  const priorityEntries = roster
                    ? Object.entries(roster).filter(([, st]) => st.priority)
                    : [];
                  return priorityEntries.length > 0 ? (
                    <div style={{ marginBottom: 12 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ka-gray-600)' }}>
                        Currently priority ({priorityEntries.length}):
                      </span>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                        {priorityEntries.map(([rid, st]) => (
                          <span key={rid} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#fef3c7', color: '#d97706' }}>
                            {st.name || st.sisId}
                            <span
                              style={{ cursor: 'pointer' }}
                              onClick={async () => {
                                await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', rid), { priority: false }, { merge: true });
                                setRoster(r => ({ ...r, [rid]: { ...r[rid], priority: false } }));
                              }}
                            >×</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Name search — add one at a time */}
                <div style={{ marginBottom: 14 }}>
                  <span style={s.label}>Search by name</span>
                  <div style={{ position: 'relative', maxWidth: 320 }}>
                    <input
                      style={{ ...s.input, width: '100%', boxSizing: 'border-box' }}
                      value={prioritySearch}
                      onChange={e => {
                        setPrioritySearch(e.target.value);
                        setPrioritySuggestions(searchRosterByName(e.target.value).filter(([, st]) => !st.priority));
                      }}
                      placeholder="Type a student name…"
                      autoComplete="off"
                    />
                    {prioritySuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                        background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)',
                        borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        maxHeight: 220, overflowY: 'auto',
                      }}>
                        {prioritySuggestions.map(([rid, st]) => (
                          <div
                            key={rid}
                            onClick={() => markPriorityOne(rid)}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--ka-gray-100)' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fffbeb'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontWeight: 500 }}>{st.name}</span>
                            <span style={{ fontSize: 11, color: 'var(--ka-gray-400)', marginLeft: 6 }}>
                              Grade {st.grade} · {st.sisId}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--ka-gray-400)', margin: '5px 0 0' }}>Click a name to mark as priority immediately.</p>
                </div>

                {/* Bulk SIS ID paste */}
                <div style={{ borderTop: '1px solid var(--ka-gray-100)', paddingTop: 12 }}>
                  <span style={s.label}>Bulk paste SIS IDs</span>
                  <textarea
                    style={{
                      width: '100%', minHeight: 80, padding: 10, fontSize: 13,
                      border: '1px solid #f59e0b', borderRadius: 6,
                      fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical',
                      background: '#fffbeb',
                    }}
                    value={priorityText}
                    onChange={e => setPriorityText(e.target.value)}
                    placeholder={'12345\n23456\n34567\n…'}
                  />
                  <div style={{ ...s.row, marginTop: 10 }}>
                    <button style={{ ...s.btn(), background: '#d97706' }} onClick={bulkPriority} disabled={prioritySaving || !priorityText.trim()}>
                      {prioritySaving ? 'Saving…' : `Mark ${priorityText.trim() ? priorityText.trim().split(/[\s,]+/).filter(Boolean).length : 0} as Priority`}
                    </button>
                    <button style={s.btn('ghost')} onClick={() => { setPriorityOpen(null); setPriorityText(''); setPrioritySearch(''); setPrioritySuggestions([]); }}>Close</button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Ineligible panel ── */}
            {ineligibleOpen === docId && (
              <div style={{ borderTop: '1px solid var(--ka-gray-100)', paddingTop: 14 }}>
                <div style={s.sectionTitle}>🚫 Ineligible for {actName(docId, act)}</div>
                <p style={{ fontSize: 12, color: 'var(--ka-gray-500)', margin: '0 0 10px' }}>
                  Students listed here will <strong>never be placed</strong> in this activity by the scheduler, even if it is their 1st choice.
                </p>

                {/* Current ineligible list */}
                {(act.ineligible?.length > 0) && (
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ka-gray-600)' }}>
                      Currently ineligible ({act.ineligible.length}):
                    </span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {act.ineligible.map(sid => {
                        const student = roster ? Object.values(roster).find(s => String(s.sisId) === String(sid)) : null;
                        return (
                          <span key={sid} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#fee2e2', color: '#991b1b' }}>
                            {student ? student.name : sid}
                            <span style={{ cursor: 'pointer' }} onClick={() => removeIneligible(docId, sid)}>×</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Name search — add one at a time */}
                <div style={{ marginBottom: 14 }}>
                  <span style={s.label}>Search by name</span>
                  <div style={{ position: 'relative', maxWidth: 320 }}>
                    <input
                      style={{ ...s.input, width: '100%', boxSizing: 'border-box' }}
                      value={ineligibleSearch}
                      onChange={e => {
                        setIneligibleSearch(e.target.value);
                        const ineligSet = new Set(act.ineligible?.map(String) || []);
                        setIneligibleSuggestions(
                          searchRosterByName(e.target.value).filter(([, st]) => !ineligSet.has(String(st.sisId || '')))
                        );
                      }}
                      placeholder="Type a student name…"
                      autoComplete="off"
                    />
                    {ineligibleSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                        background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)',
                        borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        maxHeight: 220, overflowY: 'auto',
                      }}>
                        {ineligibleSuggestions.map(([rid, st]) => (
                          <div
                            key={rid}
                            onClick={() => markIneligibleOne(docId, rid)}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--ka-gray-100)' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontWeight: 500 }}>{st.name}</span>
                            <span style={{ fontSize: 11, color: 'var(--ka-gray-400)', marginLeft: 6 }}>
                              Grade {st.grade} · {st.sisId}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--ka-gray-400)', margin: '5px 0 0' }}>Click a name to mark as ineligible immediately. Already-ineligible students are hidden.</p>
                </div>

                {/* Bulk SIS ID paste */}
                <div style={{ borderTop: '1px solid var(--ka-gray-100)', paddingTop: 12 }}>
                  <span style={s.label}>Bulk paste SIS IDs</span>
                  <textarea
                    style={{
                      width: '100%', minHeight: 80, padding: 10, fontSize: 13,
                      border: '1px solid #f87171', borderRadius: 6,
                      fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical',
                      background: '#fff5f5',
                    }}
                    value={ineligibleText}
                    onChange={e => setIneligibleText(e.target.value)}
                    placeholder={'12345\n23456\n34567\n…'}
                  />
                  <div style={{ ...s.row, marginTop: 10 }}>
                    <button style={{ ...s.btn(), background: '#dc2626' }} onClick={() => bulkIneligible(docId)} disabled={ineligibleSaving || !ineligibleText.trim()}>
                      {ineligibleSaving ? 'Saving…' : `Add ${ineligibleText.trim() ? ineligibleText.trim().split(/[\s,]+/).filter(Boolean).length : 0} to Ineligible`}
                    </button>
                    <button style={s.btn('ghost')} onClick={() => { setIneligibleOpen(null); setIneligibleText(''); setIneligibleSearch(''); setIneligibleSuggestions([]); }}>Close</button>
                  </div>
                </div>
              </div>
            )}

            {isOpen && (
              <div style={{ borderTop: '1px solid var(--ka-gray-100)', paddingTop: 14 }}>
                <div style={s.sectionTitle}>Add Staff to {actName(docId, act)} — Session {act.session || 1}</div>
                <div style={s.row}>
                  <div>
                    <span style={s.label}>Type</span>
                    <select style={s.select} value={staffType} onChange={e => { setStaffType(e.target.value); setStaffName(''); setStaffSuggestions([]); }}>
                      <option value="teacher">Teacher Leader</option>
                      <option value="coach">Outside Coach</option>
                    </select>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <span style={s.label}>Name</span>
                    <input
                      style={{ ...s.input, width: 220 }}
                      value={staffName}
                      onChange={e => handleStaffNameChange(e.target.value)}
                      placeholder={staffType === 'teacher' ? 'Start typing to search staff…' : 'Full name'}
                      autoComplete="off"
                    />
                    {staffSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, zIndex: 50,
                        background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)',
                        borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        minWidth: 220, maxHeight: 200, overflowY: 'auto',
                      }}>
                        {staffSuggestions.map(m => (
                          <div
                            key={m.id}
                            onClick={() => pickSuggestion(m)}
                            style={{
                              padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                              borderBottom: '1px solid var(--ka-gray-100)',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--ka-gray-50)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ fontWeight: 500 }}>{m.name || m.displayName}</div>
                            {m.title && <div style={{ fontSize: 11, color: 'var(--ka-gray-500)' }}>{m.title}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <button style={s.btn()} onClick={() => addStaff(docId)}>Add</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 3 — Scheduler
// ═══════════════════════════════════════════════════════════════════════════
// The scheduler treats each session of an activity independently.
// Students' choices are matched against base activity names; they may land
// in any available session of their chosen activity.
function runScheduler(roster, activities) {
  // Build counts per docId
  const counts = {};
  Object.keys(activities).forEach(id => { counts[id] = 0; });

  const result = {};
  Object.entries(roster).forEach(([id, s]) => {
    result[id] = { ...s, placed: null, placedDocId: null, placedRound: null };
  });

  // Build per-activity ineligible sets (keyed by both docId and base name for fast lookup)
  // ineligibleFor[docId] = Set of sisIds
  const ineligibleFor = {};
  Object.entries(activities).forEach(([docId, act]) => {
    if (act.ineligible?.length > 0) {
      ineligibleFor[docId] = new Set(act.ineligible.map(s => String(s)));
    }
  });

  // Helper: is student ineligible for a given session?
  const isIneligible = (student, sessionDocId) => {
    const set = ineligibleFor[sessionDocId];
    if (!set) return false;
    return set.has(String(student.sisId || ''));
  };

  // Only place students with status 'active' or 'offseason-opted-in'
  const eligibleAll = Object.entries(result).filter(([, s]) =>
    s.status === 'active' || s.status === 'offseason-opted-in'
  );

  // Priority students go first within every round so they get their 1st choice before others
  const eligible = [
    ...eligibleAll.filter(([, s]) => s.priority === true),
    ...eligibleAll.filter(([, s]) => s.priority !== true),
  ];

  // For a given base name, return docIds sorted by session preference.
  // Day students: session 1 first (early session).
  // Boarding students (or unknown): session 2+ first, then session 1 as fallback.
  const sessionsFor = (name, studentCohort) => {
    const sessions = Object.entries(activities)
      .filter(([, a]) => a.name === name)
      .sort(([, a], [, b]) => a.session - b.session)
      .map(([id]) => id);
    if (studentCohort === 'Boarding' && sessions.length > 1) {
      // Prefer later sessions for boarding students; keep s1 as final fallback
      return [...sessions.slice(1), sessions[0]];
    }
    return sessions; // Day students (or unknown) get s1 first
  };

  // Pre-compute deduplicated choices for every student so the scheduler uses
  // only unique choices even if the roster doc has duplicates stored.
  const deduped = {};
  eligible.forEach(([id, student]) => {
    deduped[id] = dedupeChoices(student.choice1, student.choice2, student.choice3);
  });

  for (let round = 1; round <= 3; round++) {
    const choiceKey = ['choice1', 'choice2', 'choice3'][round - 1];
    for (const [id] of eligible) {
      if (result[id].placed) continue;
      const choiceName = deduped[id][choiceKey];
      if (!choiceName) continue;
      // Try each session of the chosen activity in order, skipping ineligible sessions
      const sessions = sessionsFor(choiceName, result[id].cohort);
      for (const sessionDocId of sessions) {
        if (isIneligible(result[id], sessionDocId)) continue; // blocked from this activity
        const cap = activities[sessionDocId]?.capacity || 20;
        if (counts[sessionDocId] < cap) {
          result[id].placed = choiceName;
          result[id].placedDocId = sessionDocId;
          result[id].placedRound = round;
          counts[sessionDocId]++;
          break;
        }
      }
    }
  }

  // Mark unplaced
  eligibleAll.forEach(([id]) => {
    if (!result[id].placed) result[id] = { ...result[id], status: 'unplaced' };
  });

  return { result, counts };
}

function SchedulerTab({ roster, setRoster, activities, activityList, config, setConfig, canManage, flash, committed, saving, setSaving }) {
  const [preview, setPreview] = useState(null);
  const [overrideId, setOverrideId] = useState('');
  const [overrideDocId, setOverrideDocId] = useState('');

  const eligible = Object.values(roster).filter(s => s.status === 'active' || s.status === 'offseason-opted-in');
  const inPool = eligible.length;

  const runPreview = () => {
    const { result, counts } = runScheduler(roster, activities);
    setPreview({ result, counts });
  };

  const applyAndSave = async () => {
    if (!preview) return;
    setSaving(true);
    const batch = writeBatch(db);
    Object.entries(preview.result).forEach(([id, data]) => {
      batch.set(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), {
        placed: data.placed,
        placedDocId: data.placedDocId || null,
        placedRound: data.placedRound,
        status: data.status,
        manualOverride: data.manualOverride || null,
      }, { merge: true });
    });
    await batch.commit();
    setRoster(preview.result);
    setPreview(null);
    flash('success', 'Schedule saved to Firestore. You can run it again or commit when ready.');
    setSaving(false);
  };

  const commitSchedule = async () => {
    if (!window.confirm('Commit this schedule? Students and teachers will be able to see assignments. You can still make manual adjustments after.')) return;
    setSaving(true);
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID), { status: 'committed' }, { merge: true });
    setConfig(c => ({ ...c, status: 'committed' }));
    flash('success', 'Schedule committed! Students and teachers can now see their assignments.');
    setSaving(false);
  };

  const resetPlacements = async () => {
    if (!window.confirm('Reset all placements? This will clear every assignment and set all placed/unplaced students back to Active. This cannot be undone.')) return;
    setSaving(true);
    const toReset = Object.entries(roster).filter(([, s]) => s.placed || s.status === 'unplaced');
    const chunks = [];
    for (let i = 0; i < toReset.length; i += 500) chunks.push(toReset.slice(i, i + 500));
    for (const chunk of chunks) {
      const b = writeBatch(db);
      chunk.forEach(([id]) => b.set(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), {
        placed: null, placedDocId: null, placedRound: null, status: 'active', manualOverride: null,
      }, { merge: true }));
      await b.commit();
    }
    // Also un-commit the season so it goes back to draft
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID), { status: 'draft' }, { merge: true });
    setConfig(c => ({ ...c, status: 'draft' }));
    setRoster(r => {
      const next = { ...r };
      toReset.forEach(([id]) => {
        next[id] = { ...next[id], placed: null, placedDocId: null, placedRound: null, status: 'active', manualOverride: null };
      });
      return next;
    });
    setPreview(null);
    flash('success', `Reset ${toReset.length} placements. Roster is back to Active pool.`);
    setSaving(false);
  };

  const applyManualOverride = async () => {
    if (!overrideId || !overrideDocId) return;
    const student = roster[overrideId];
    if (!student) return flash('warn', 'Student ID not found.');
    const act = activities[overrideDocId];
    await updateDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', overrideId), {
      placed: act?.name || overrideDocId,
      placedDocId: overrideDocId,
      placedRound: null,
      manualOverride: overrideDocId,
      status: 'active',
    });
    setRoster(r => ({ ...r, [overrideId]: {
      ...r[overrideId],
      placed: act?.name || overrideDocId,
      placedDocId: overrideDocId,
      placedRound: null,
      manualOverride: overrideDocId,
    }}));
    setOverrideId(''); setOverrideDocId('');
    flash('success', `Manually placed ${student.name || overrideId} in ${act?.name} Session ${act?.session}.`);
  };

  const source = preview ? preview.result : roster;

  // Count per docId
  const actCounts = {};
  Object.keys(activities).forEach(id => { actCounts[id] = 0; });
  Object.values(source).forEach(s => { if (s.placedDocId) actCounts[s.placedDocId] = (actCounts[s.placedDocId] || 0) + 1; });

  const placed   = Object.values(source).filter(s => s.placed).length;
  const unplaced = Object.values(source).filter(s => s.status === 'unplaced').length;
  const r1 = Object.values(source).filter(s => s.placedRound === 1).length;
  const r2 = Object.values(source).filter(s => s.placedRound === 2).length;
  const r3 = Object.values(source).filter(s => s.placedRound === 3).length;

  return (
    <div>
      {/* Controls */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Run Scheduler</div>
        <p style={{ fontSize: 13, color: 'var(--ka-gray-500)', marginBottom: 14 }}>
          {inPool} students in pool (active + offseason opted-in). Basketball, exempt, and offseason students are excluded unless their status is changed.
        </p>
        <div style={s.row}>
          <button style={s.btn()} onClick={runPreview}>▶ Run Scheduler (Preview)</button>
          {preview && <button style={s.btn('success')} onClick={applyAndSave} disabled={saving}>💾 Save Results</button>}
          {!preview && placed > 0 && !committed && canManage && (
            <button style={s.btn('success')} onClick={commitSchedule} disabled={saving}>✓ Commit Schedule</button>
          )}
          {committed && canManage && (
            <button style={s.btn('ghost')} onClick={commitSchedule}>Re-commit</button>
          )}
          {placed > 0 && canManage && (
            <button style={s.btn('danger')} onClick={resetPlacements} disabled={saving}>↺ Reset All Placements</button>
          )}
        </div>
        {preview && (
          <div style={{ ...s.warn, marginTop: 14, marginBottom: 0 }}>
            This is a preview — click "Save Results" to persist to Firestore.
          </div>
        )}
      </div>

      {/* Stats */}
      {(preview || placed > 0) && (
        <div style={{ ...s.card, padding: 14 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={s.stat}><div style={s.statN}>{placed}</div><div style={s.statL}>Placed</div></div>
            <div style={s.stat}><div style={{ ...s.statN, color: '#dc2626' }}>{unplaced}</div><div style={s.statL}>Unplaced</div></div>
            <div style={s.stat}><div style={{ ...s.statN, color: '#059669' }}>{r1}</div><div style={s.statL}>1st Choice</div></div>
            <div style={s.stat}><div style={{ ...s.statN, color: '#d97706' }}>{r2}</div><div style={s.statL}>2nd Choice</div></div>
            <div style={s.stat}><div style={{ ...s.statN, color: '#6b7280' }}>{r3}</div><div style={s.statL}>3rd Choice</div></div>
          </div>
        </div>
      )}

      {/* Activity load */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Activity Loads {preview ? '(Preview)' : '(Current)'}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Activity</th>
                <th style={s.th}>Session</th>
                <th style={s.th}>Enrolled</th>
                <th style={s.th}>Capacity</th>
                <th style={s.th}>Available</th>
                <th style={s.th}>Fill</th>
                <th style={s.th}>Staff</th>
              </tr>
            </thead>
            <tbody>
              {activityList.map(([docId, act]) => {
                const cap = act.capacity || 20;
                const enrolled = actCounts[docId] || 0;
                const pct = Math.round((enrolled / cap) * 100);
                const full = enrolled >= cap;
                return (
                  <tr key={docId}>
                    <td style={s.td}><strong>{actName(docId, act)}</strong></td>
                    <td style={s.td}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                        background: act.session === 1 ? '#dbeafe' : '#ede9fe',
                        color: act.session === 1 ? '#1d4ed8' : '#7c3aed',
                      }}>S{act.session}</span>
                    </td>
                    <td style={s.td}>{enrolled}</td>
                    <td style={s.td}>{cap}</td>
                    <td style={s.td}>{full ? <span style={{ color: '#dc2626', fontWeight: 600 }}>FULL</span> : cap - enrolled}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 80, height: 6, background: 'var(--ka-gray-200)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: full ? '#dc2626' : '#2563eb', borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--ka-gray-500)' }}>{pct}%</span>
                      </div>
                    </td>
                    <td style={s.td}>
                      {(act.staff || []).map((m, i) => (
                        <span key={i} style={{ ...s.badge(m.type === 'coach' ? '#059669' : '#2563eb'), marginRight: 4 }}>{m.name}</span>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual overrides */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Manual Placement Override</div>
        <p style={{ fontSize: 13, color: 'var(--ka-gray-500)', marginBottom: 12 }}>
          Override a student's placement. Enter their SIS ID and select the specific activity session.
        </p>
        <div style={s.row}>
          <div>
            <span style={s.label}>Student SIS ID</span>
            <input style={{ ...s.input, width: 140 }} value={overrideId} onChange={e => setOverrideId(e.target.value)} placeholder="e.g. 166985" />
          </div>
          <div>
            <span style={s.label}>Place in Activity / Session</span>
            <select style={s.select} value={overrideDocId} onChange={e => setOverrideDocId(e.target.value)}>
              <option value="">— select —</option>
              {activityList.map(([docId, act]) => (
                <option key={docId} value={docId}>{actName(docId, act)} — Session {act.session}</option>
              ))}
            </select>
          </div>
          <div style={{ marginTop: 16 }}>
            <button style={s.btn()} onClick={applyManualOverride}>Apply Override</button>
          </div>
        </div>

        {/* Unplaced students quick-assign */}
        {Object.values(source).some(s => s.status === 'unplaced') && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#dc2626' }}>Unplaced Students</div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Grade</th>
                  <th style={s.th}>Choices</th>
                  <th style={s.th}>Assign</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(source).filter(([, st]) => st.status === 'unplaced').map(([id, student]) => (
                  <UnplacedRow key={id} id={id} student={student} activities={activities} activityList={activityList} actCounts={actCounts} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function UnplacedRow({ id, student, activities, activityList, actCounts }) {
  const [pick, setPick] = useState('');
  const assign = async () => {
    if (!pick) return;
    const act = activities[pick];
    await updateDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', id), {
      placed: act?.name || pick,
      placedDocId: pick,
      placedRound: null,
      manualOverride: pick,
      status: 'active',
    });
    setPick('');
  };
  return (
    <tr>
      <td style={s.td}>{student.name || id}</td>
      <td style={s.td}>{student.grade || '—'}</td>
      <td style={{ ...s.td, fontSize: 12 }}>
        {[student.choice1, student.choice2, student.choice3].filter(Boolean).join(' → ')}
      </td>
      <td style={s.td}>
        <div style={{ display: 'flex', gap: 6 }}>
          <select style={{ ...s.select, fontSize: 12 }} value={pick} onChange={e => setPick(e.target.value)}>
            <option value="">— assign —</option>
            {activityList.map(([docId, act]) => {
              const cap = act.capacity || 20;
              const enrolled = actCounts[docId] || 0;
              return <option key={docId} value={docId}>{actName(docId, act)} S{act.session} ({enrolled}/{cap})</option>;
            })}
          </select>
          <button style={{ ...s.btn(), padding: '5px 10px', fontSize: 12 }} onClick={assign}>Place</button>
        </div>
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 4 — Results
// ═══════════════════════════════════════════════════════════════════════════
function ResultsTab({ roster, rosterList, activities, activityList, config, flash, canManage, setRoster }) {
  const [view, setView] = useState('byActivity');
  const [filterGrade, setFilterGrade] = useState('all');
  const [expandedActivities, setExpandedActivities] = useState({}); // docId → bool

  const committed = config?.status === 'committed';

  // Group rosterList by placedDocId
  const byDocId = {};
  activityList.forEach(([docId]) => { byDocId[docId] = []; });
  rosterList.forEach(s => {
    const key = s.placedDocId || (s.placed ? Object.entries(activities).find(([, a]) => a.name === s.placed)?.[0] : null);
    if (key && byDocId[key] !== undefined) byDocId[key].push(s);
  });

  const toggleActivity = (docId) => setExpandedActivities(e => ({ ...e, [docId]: !e[docId] }));

  // Unplace a student — works from both views
  const unplaceStudent = async (student) => {
    if (!window.confirm(`Remove ${student.name}'s placement from ${student.placed}?`)) return;
    await setDoc(doc(db, 'cocoSeasons', SEASON_ID, 'roster', student.id), {
      placed: null, placedDocId: null, placedRound: null,
      status: 'active', manualOverride: null,
    }, { merge: true });
    setRoster(r => ({
      ...r,
      [student.id]: { ...r[student.id], placed: null, placedDocId: null, placedRound: null, status: 'active', manualOverride: null },
    }));
    flash('success', `Removed ${student.name}'s placement.`);
  };

  const downloadCSV = () => {
    const rows = [['Name', 'SIS ID', 'Grade', 'Email', 'Status', 'Assigned Activity', 'Session', 'Round', 'Manual Override', '1st Choice', '2nd Choice', '3rd Choice']];
    rosterList.forEach(s => {
      const actDoc = s.placedDocId ? activities[s.placedDocId] : null;
      rows.push([
        s.name, s.sisId, s.grade, s.email,
        STATUS_LABELS[s.status]?.label || s.status,
        s.placed || '',
        actDoc?.session || '',
        s.placedRound || '',
        s.manualOverride ? 'Yes' : 'No',
        s.choice1, s.choice2, s.choice3,
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'coco-assignments.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {!committed && (
        <div style={s.warn}>Schedule is not yet committed. Only admins can see this view. Commit the schedule in the Scheduler tab to make it visible to students and teachers.</div>
      )}

      <div style={{ ...s.card, padding: 14, marginBottom: 16 }}>
        <div style={s.row}>
          <button style={s.tab(view === 'byActivity')} onClick={() => setView('byActivity')}>By Activity</button>
          <button style={s.tab(view === 'byStudent')} onClick={() => setView('byStudent')}>By Student</button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <select style={s.select} value={filterGrade} onChange={e => setFilterGrade(e.target.value)}>
              <option value="all">All grades</option>
              {['9', '10', '11', '12'].map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
            <button style={s.btn('ghost')} onClick={downloadCSV}>⬇ CSV Export</button>
          </div>
        </div>
      </div>

      {view === 'byActivity' && (
        <div>
          {activityList.map(([docId, act]) => {
            const allStudents = byDocId[docId] || [];
            const students = allStudents.filter(s => filterGrade === 'all' || s.grade === filterGrade);
            const cap = act.capacity || 20;
            const staff = act.staff || [];
            const isExpanded = !!expandedActivities[docId];
            const pct = Math.round((allStudents.length / cap) * 100);
            return (
              <div key={docId} style={s.card}>
                {/* Header row — always visible, click to expand */}
                <div
                  style={{ ...s.row, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleActivity(docId)}
                >
                  <span style={{ fontSize: 16, color: 'var(--ka-gray-400)', marginRight: 2 }}>
                    {isExpanded ? '▾' : '▸'}
                  </span>
                  <div style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>{actName(docId, act)}</div>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                    background: act.session === 1 ? '#dbeafe' : '#ede9fe',
                    color: act.session === 1 ? '#1d4ed8' : '#7c3aed',
                  }}>Session {act.session}</span>
                  <span style={s.badge(allStudents.length >= cap ? '#dc2626' : allStudents.length >= cap * 0.9 ? '#d97706' : '#059669')}>
                    {allStudents.length} / {cap}
                  </span>
                  {/* Capacity fill bar */}
                  <div style={{ width: 80, height: 6, background: 'var(--ka-gray-200)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 3,
                      width: `${Math.min(pct, 100)}%`,
                      background: allStudents.length >= cap ? '#dc2626' : allStudents.length >= cap * 0.9 ? '#d97706' : '#059669',
                    }} />
                  </div>
                  {staff.map((m, i) => (
                    <span key={i} style={s.badge(m.type === 'coach' ? '#059669' : '#2563eb')}>
                      {m.name} · {m.type === 'coach' ? 'Coach' : 'Teacher'}
                    </span>
                  ))}
                </div>

                {/* Expanded student list */}
                {isExpanded && (
                  <div style={{ marginTop: 12, borderTop: '1px solid var(--ka-gray-100)', paddingTop: 12 }}>
                    {students.length === 0 ? (
                      <p style={{ fontSize: 13, color: 'var(--ka-gray-400)', margin: 0 }}>
                        {filterGrade !== 'all' ? `No Grade ${filterGrade} students here.` : 'No students assigned.'}
                      </p>
                    ) : (
                      <table style={s.table}>
                        <thead>
                          <tr>
                            <th style={s.th}>Name</th>
                            <th style={s.th}>Grade</th>
                            <th style={s.th}>SIS ID</th>
                            <th style={s.th}>Round</th>
                            <th style={s.th}>Override</th>
                            {canManage && <th style={s.th}></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {students
                            .sort((a, b) => Number(a.grade) - Number(b.grade) || (a.name || '').localeCompare(b.name || ''))
                            .map(student => (
                              <tr key={student.id}>
                                <td style={s.td}><strong>{student.name || '—'}</strong></td>
                                <td style={s.td}>{student.grade || '—'}</td>
                                <td style={s.td}>{student.sisId || '—'}</td>
                                <td style={s.td}>
                                  {student.placedRound
                                    ? <span style={s.badge(student.placedRound === 1 ? '#059669' : student.placedRound === 2 ? '#d97706' : '#6b7280')}>
                                        {student.placedRound === 1 ? '1st' : student.placedRound === 2 ? '2nd' : '3rd'} choice
                                      </span>
                                    : '—'}
                                </td>
                                <td style={s.td}>
                                  {student.manualOverride ? <span style={s.badge('#7c3aed')}>Manual</span> : '—'}
                                </td>
                                {canManage && (
                                  <td style={{ ...s.td, textAlign: 'right' }}>
                                    <button
                                      title="Remove from this activity"
                                      style={{ ...s.btn('danger'), padding: '3px 10px', fontSize: 12 }}
                                      onClick={() => unplaceStudent(student)}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === 'byStudent' && (
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Name</th>
                <th style={s.th}>Grade</th>
                <th style={s.th}>SIS ID</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Assigned</th>
                <th style={s.th}>Session</th>
                <th style={s.th}>Round</th>
                {canManage && <th style={s.th}></th>}
              </tr>
            </thead>
            <tbody>
              {rosterList
                .filter(s => filterGrade === 'all' || s.grade === filterGrade)
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map(student => {
                  const info = STATUS_LABELS[student.status] || { label: student.status, color: '#6b7280' };
                  const actDoc = student.placedDocId ? activities[student.placedDocId] : null;
                  return (
                    <tr key={student.id}>
                      <td style={s.td}><strong>{student.name || '—'}</strong></td>
                      <td style={s.td}>{student.grade || '—'}</td>
                      <td style={s.td}>{student.sisId || '—'}</td>
                      <td style={s.td}><span style={s.badge(info.color)}>{info.label}</span></td>
                      <td style={s.td}>{student.placed || <span style={{ color: 'var(--ka-gray-400)' }}>—</span>}</td>
                      <td style={s.td}>
                        {actDoc
                          ? <span style={{
                              fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                              background: actDoc.session === 1 ? '#dbeafe' : '#ede9fe',
                              color: actDoc.session === 1 ? '#1d4ed8' : '#7c3aed',
                            }}>S{actDoc.session}</span>
                          : '—'}
                      </td>
                      <td style={s.td}>
                        {student.placedRound
                          ? <span style={s.badge(student.placedRound === 1 ? '#059669' : student.placedRound === 2 ? '#d97706' : '#6b7280')}>
                              {student.placedRound === 1 ? '1st' : student.placedRound === 2 ? '2nd' : '3rd'}
                            </span>
                          : student.manualOverride ? <span style={s.badge('#7c3aed')}>Manual</span> : '—'}
                      </td>
                      {canManage && (
                        <td style={{ ...s.td, textAlign: 'right' }}>
                          {student.placed && (
                            <button
                              title="Remove placement"
                              style={{ ...s.btn('danger'), padding: '3px 10px', fontSize: 12 }}
                              onClick={() => unplaceStudent(student)}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}